import React, { useState, useEffect, useRef } from 'react';
import { AZURE } from '../../services/api';
import { Copy, CheckCircle2 } from 'lucide-react';
import { COLORS } from '../../components/governance/governanceTheme';
import { listPlannerPlans, generateTasks, sendBuilderMessage, getPlannerSnapshot } from '../../services/governance';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BUILDER_LABELS = {
  pt: {
    selectPlan: '📋 Selecionar Plano', refresh: 'Atualizar', noPlans: 'Nenhum plano disponível.',
    createPlanHint: 'Crie um plano no Planner primeiro.',
    selectItems: (n) => `📦 Selecionar Items (${n} selecionados)`,
    noItemsSnapshot: 'Nenhum item disponível no snapshot.', selectPlanFirst: 'Selecione um plano primeiro.',
    top3: '⭐ Top 3', all: '✅ Todos', clear: '❌ Limpar',
    copilotPrompt: '🤖 Copilot Prompt', copied: 'Copiado!', copyPrompt: 'Copiar Prompt',
    dependencies: 'Dependências:', emptyMsg: '(mensagem vazia)', noBuilderReply: '(sem resposta do Builder)',
    statusLabels: { pending: 'Pendente', in_progress: 'Em Andamento', done: 'Concluída', blocked: 'Bloqueada' },
    hide: 'Ocultar', viewAll: 'Ver Todos', legacyCopy: '📋 Copiar Prompt',
    subtitle: 'Chat + Tarefas + Prompts Copilot-ready (Founder-only)',
    tabTasks: (n) => `Tarefas (${n})`,
    tasksHeader: (n) => `📋 Tarefas (${n})`,
    generateTasks: '➕ Gerar Tarefas', noTasksYet: 'Nenhuma tarefa gerada ainda',
    noTasksHint: 'Clique em "Gerar Tarefas" ou peça ao Builder no chat',
    cost: 'Custo', estimated: 'Estimado:', actual: 'Realizado:', delta: 'Delta:',
    status: 'Status', pendingCount: 'Pendentes', inProgress: 'Em Andamento', doneCount: 'Concluídas', blockedCount: 'Bloqueadas',
    quickActions: 'Ações Rápidas', createPlan: 'Criar Plano', viewSnapshot: 'Ver Snapshot',
    placeholder: 'Pergunte ao Builder...', send: 'Enviar',
    selectPlanAlert: 'Selecione um plano primeiro', selectItemsAlert: 'Selecione pelo menos 1 item do snapshot',
  },
  en: {
    selectPlan: '📋 Select Plan', refresh: 'Refresh', noPlans: 'No plans available.',
    createPlanHint: 'Create a plan in the Planner first.',
    selectItems: (n) => `📦 Select Items (${n} selected)`,
    noItemsSnapshot: 'No items available in the snapshot.', selectPlanFirst: 'Select a plan first.',
    top3: '⭐ Top 3', all: '✅ All', clear: '❌ Clear',
    copilotPrompt: '🤖 Copilot Prompt', copied: 'Copied!', copyPrompt: 'Copy Prompt',
    dependencies: 'Dependencies:', emptyMsg: '(empty message)', noBuilderReply: '(no Builder reply)',
    statusLabels: { pending: 'Pending', in_progress: 'In Progress', done: 'Done', blocked: 'Blocked' },
    hide: 'Hide', viewAll: 'View All', legacyCopy: '📋 Copy Prompt',
    subtitle: 'Chat + Tasks + Copilot-ready Prompts (Founder-only)',
    tabTasks: (n) => `Tasks (${n})`,
    tasksHeader: (n) => `📋 Tasks (${n})`,
    generateTasks: '➕ Generate Tasks', noTasksYet: 'No tasks generated yet',
    noTasksHint: 'Click "Generate Tasks" or ask the Builder in chat',
    cost: 'Cost', estimated: 'Estimated:', actual: 'Actual:', delta: 'Delta:',
    status: 'Status', pendingCount: 'Pending', inProgress: 'In Progress', doneCount: 'Done', blockedCount: 'Blocked',
    quickActions: 'Quick Actions', createPlan: 'Create Plan', viewSnapshot: 'View Snapshot',
    placeholder: 'Ask the Builder...', send: 'Send',
    selectPlanAlert: 'Select a plan first', selectItemsAlert: 'Select at least 1 item from the snapshot',
  }
};

// Plan Selector Component
const PlanSelector = ({ plans, selectedPlanId, onSelect, onRefresh, loading, t }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>
          {t.selectPlan}
        </h3>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {loading ? '⏳' : '🔄'} {t.refresh}
        </button>
      </div>

      {plans.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">{t.noPlans}</p>
          <p className="text-xs mt-2">{t.createPlanHint}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {plans.map(plan => (
            <button
              key={plan.id}
              onClick={() => onSelect(plan.id)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedPlanId === plan.id
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{plan.goal}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Plan ID: {plan.id} • Criado: {new Date(plan.created_at).toLocaleDateString('pt-BR')}
                  </div>
                  {plan.snapshot_id && (
                    <div className="text-xs text-green-600 mt-1 font-mono">
                      Snapshot: {plan.snapshot_id.substring(0, 8)}...
                    </div>
                  )}
                </div>
                {selectedPlanId === plan.id && (
                  <div className="ml-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-3 text-xs">
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                  {plan.items_count || 0} items
                </span>
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                  {plan.horizon_days}d
                </span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                  {plan.status}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Item Selector Component (for task generation)
const ItemSelector = ({ snapshot, selectedIndices, onSelectionChange, loading, t }) => {
  if (!snapshot || !snapshot.canvas || snapshot.canvas.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>
          📦 {t.selectItems(0).split('(')[0].trim()}
        </h3>
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">{t.noItemsSnapshot}</p>
          <p className="text-xs mt-2">{t.selectPlanFirst}</p>
        </div>
      </div>
    );
  }

  // Group items by track
  const byTrack = snapshot.canvas.reduce((acc, item, index) => {
    const track = item.track || 'Backend';
    if (!acc[track]) acc[track] = [];
    acc[track].push({ ...item, array_index: index });
    return acc;
  }, {});

  const toggleSelection = (arrayIndex) => {
    const newSelection = selectedIndices.includes(arrayIndex)
      ? selectedIndices.filter(i => i !== arrayIndex)
      : [...selectedIndices, arrayIndex];
    onSelectionChange(newSelection);
  };

  const selectTop3 = () => {
    // Select top 3 by impact_score or first 3
    const top3 = snapshot.canvas
      .slice(0, 3)
      .map((_, idx) => idx);
    onSelectionChange(top3);
  };

  const selectAll = () => {
    onSelectionChange(snapshot.canvas.map((_, idx) => idx));
  };

  const clearSelection = () => {
    onSelectionChange([]);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>
          {t.selectItems(selectedIndices.length)}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={selectTop3}
            disabled={loading}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t.top3}
          </button>
          <button
            onClick={selectAll}
            disabled={loading}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t.all}
          </button>
          <button
            onClick={clearSelection}
            disabled={loading}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t.clear}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(byTrack).map(([track, items]) => (
          <div key={track} className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-700 mb-3">{track}</h4>
            <div className="space-y-2">
              {items.map(item => (
                <label
                  key={item.array_index}
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedIndices.includes(item.array_index)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIndices.includes(item.array_index)}
                    onChange={() => toggleSelection(item.array_index)}
                    className="mt-1 w-5 h-5 text-green-600"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{item.title}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {item.area} • {item.canvas_status}
                      {item.impact_score && ` • Impact: ${item.impact_score}`}
                      {item.effort_points && ` • Effort: ${item.effort_points}`}
                    </div>
                    {item.current_state_summary && (
                      <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {item.current_state_summary}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Task Prompt Card Component
const TaskPromptCard = ({ task, t }) => {
  const [copied, setCopied] = useState(false);

  const copyPrompt = () => {
    if (task.copilot_prompt) {
      navigator.clipboard.writeText(task.copilot_prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const priorityColors = {
    high: 'border-red-500 bg-red-50',
    medium: 'border-yellow-500 bg-yellow-50',
    low: 'border-green-500 bg-green-50'
  };

  return (
    <div className={`border-l-4 ${priorityColors[task.priority]} rounded-lg p-4 mb-4`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-bold text-gray-900">{task.title}</h4>
          <p className="text-sm text-gray-600 mt-1">{task.description}</p>
        </div>
        <div className="flex gap-2">
          <span className="text-xs px-2 py-1 bg-gray-100 rounded font-semibold">
            {task.priority.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-3">
        ${task.estimated_cost_usd?.toFixed(2)} • 
        Task ID: <span className="font-mono">{task.task_id.substring(0, 8)}...</span>
      </div>

      {/* Copilot Prompt */}
      {task.copilot_prompt && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold" style={{ color: COLORS.primary }}>
              {t.copilotPrompt}
            </span>
            <button
              onClick={copyPrompt}
              className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-[#111111] to-[#374151] text-white rounded-lg text-xs font-semibold hover:opacity-90"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  {t.copied}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  {t.copyPrompt}
                </>
              )}
            </button>
          </div>
          <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto max-h-64">
            <code>{task.copilot_prompt}</code>
          </pre>
        </div>
      )}

      {/* Dependencies */}
      {task.dependencies && task.dependencies.length > 0 && (
        <div className="mt-3 text-xs text-gray-600">
          <span className="font-semibold">{t.dependencies}</span> {task.dependencies.join(', ')}
        </div>
      )}
    </div>
  );
};

// Chat Message Component
const ChatMessage = ({ message, isUser, t }) => {
  // Extrair texto com fallback robusto (SEMPRE renderiza, nunca esconde)
  const displayText = isUser 
    ? (message.message || t.emptyMsg)
    : ((message.response?.text || message.response?.reply || '').trim() || t.noBuilderReply);
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`max-w-[70%] rounded-lg px-4 py-3 ${
          isUser ? 'bg-gradient-to-r from-[#111111] to-[#374151] text-white' : 'bg-gray-100 text-gray-900'
        }`}
      >
        {/* SEMPRE renderiza, mesmo que vazio (placeholder visível) */}
        <div className="whitespace-pre-wrap">{displayText}</div>
      
        {/* Actions (botões clicáveis) */}
        {!isUser && message.response?.actions?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {message.response.actions.map((action, idx) => (
              <button
                key={idx}
                className="px-3 py-1 bg-white text-[#111111] rounded-lg text-sm font-semibold border border-gray-200 hover:bg-gray-50"
                onClick={() => {/* TODO: handle action */}}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
        
        {/* Artifacts (código, SQL, JSON) */}
        {!isUser && message.response?.artifacts?.length > 0 && (
          <div className="mt-3 space-y-2">
            {message.response.artifacts.map((artifact, idx) => (
              <div key={idx} className="bg-gray-900 rounded-lg p-3">
                <div className="text-xs text-gray-400 mb-1">{artifact.title}</div>
                <pre className="text-xs text-green-400 overflow-x-auto">
                  <code>{artifact.content}</code>
                </pre>
              </div>
            ))}
          </div>
        )}
        
        <div className="text-xs opacity-70 mt-1">
          {new Date(message.timestamp || Date.now()).toLocaleTimeString('pt-BR')}
        </div>
      </div>
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, onStatusChange, t }) => {
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  
  const priorityColors = {
    high: 'border-red-500 bg-red-50',
    medium: 'border-yellow-500 bg-yellow-50',
    low: 'border-green-500 bg-green-50'
  };
  
  const statusLabels = t.statusLabels;
  
  // Parse artifacts_json quando task atualizar
  useEffect(() => {
    if (task.artifacts_json) {
      try {
        const parsed = JSON.parse(task.artifacts_json);
        setArtifacts(parsed.artifacts || []);
      } catch (e) {
        console.error('Failed to parse artifacts_json:', e);
        setArtifacts([]);
      }
    }
  }, [task.artifacts_json]);
  
  const copyToClipboard = (text, artifactTitle = '') => {
    navigator.clipboard.writeText(text);
    alert(artifactTitle ? `"${artifactTitle}" copiado!` : 'Copiado!');
  };
  
  return (
    <div className={`border-l-4 ${priorityColors[task.priority]} rounded-lg p-4 mb-3`}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-bold text-gray-900">{task.title}</h4>
        <select
          value={task.status}
          onChange={(e) => onStatusChange(task.task_id, e.target.value)}
          className="text-sm border border-gray-300 rounded px-2 py-1"
        >
          {Object.entries(statusLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      
      <p className="text-sm text-gray-600 mb-2">{task.description}</p>
      
      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
        <span>${task.estimated_cost_usd?.toFixed(2)}</span>
        {task.actual_cost_usd && (
          <span className={task.actual_cost_usd > task.estimated_cost_usd ? 'text-red-600' : 'text-green-600'}>
            Real: ${task.actual_cost_usd.toFixed(2)}
            {task.actual_cost_usd > task.estimated_cost_usd && 
              ` (+${((task.actual_cost_usd / task.estimated_cost_usd - 1) * 100).toFixed(0)}%)`
            }
          </span>
        )}
        {artifacts.length > 0 && (
          <span className="text-[#111111] font-semibold">
            📦 {artifacts.length} artifact{artifacts.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      
      {/* ⭐ PASSO 5: Artifacts Section */}
      {artifacts.length > 0 && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Artifacts</span>
            <button
              onClick={() => setShowArtifacts(!showArtifacts)}
              className="text-xs text-[#111111] hover:underline"
            >
              {showArtifacts ? t.hide : t.viewAll}
            </button>
          </div>
          
          {showArtifacts && (
            <div className="space-y-2">
              {artifacts.map((artifact, idx) => (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold text-gray-900">{artifact.title}</div>
                      <div className="text-xs text-gray-500">
                        {artifact.type === 'copilot_prompt' && 'Copilot Prompt'}
                        {artifact.type === 'code' && `Code (${artifact.language || 'unknown'})`}
                        {artifact.type === 'sql' && 'SQL Script'}
                        {artifact.type === 'test' && `Test (${artifact.test_framework || 'unknown'})`}
                      </div>
                      {artifact.target_repo && (
                        <div className="text-xs text-gray-500">{artifact.target_repo}</div>
                      )}
                    </div>
                    <button
                      onClick={() => copyToClipboard(artifact.content, artifact.title)}
                      className="px-2 py-1 bg-[#111111] text-white rounded text-xs hover:bg-[#0b0b0c]"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <pre className="bg-gray-900 text-green-400 p-2 rounded text-xs overflow-x-auto max-h-32">
                    <code>{artifact.content}</code>
                  </pre>
                </div>
              ))}
            </div>
          )}
          
          {/* Quick Copy: Copilot Prompt */}
          {artifacts.find(a => a.type === 'copilot_prompt') && !showArtifacts && (
            <button
              onClick={() => {
                const copilotArtifact = artifacts.find(a => a.type === 'copilot_prompt');
                copyToClipboard(copilotArtifact.content, 'Copilot Prompt');
              }}
              className="mt-2 px-3 py-1 bg-[#111111] text-white rounded text-xs hover:bg-[#0b0b0c]"
            >
              📋 Copy Copilot Prompt
            </button>
          )}
        </div>
      )}
      
      {/* Legacy Copilot Prompt (backwards compat) */}
      {task.copilot_prompt && artifacts.length === 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm font-semibold text-[#111111]">
            📋 Prompt Copilot
          </summary>
          <pre className="mt-2 bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
            {task.copilot_prompt}
          </pre>
          <button
            className="mt-2 px-3 py-1 bg-[#111111] text-white rounded text-xs"
            onClick={() => copyToClipboard(task.copilot_prompt)}
          >
            {t.legacyCopy}
          </button>
        </details>
      )}
    </div>
  );
};

// Main Builder Page
const GovernanceBuilderPage = () => {
  const { lang } = useGovernanceLang();
  const t = BUILDER_LABELS[lang] || BUILDER_LABELS.pt;
  const [chatMessages, setChatMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'tasks'
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const chatEndRef = useRef(null);
  
  // Carregar plans no mount
  useEffect(() => {
    fetchPlans();
  }, []);
  
  // Carregar snapshot quando plan mudar
  useEffect(() => {
    if (selectedPlanId) {
      fetchSnapshot();
    } else {
      setSnapshot(null);
      setSelectedIndices([]);
    }
  }, [selectedPlanId]);
  
  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  
  // Welcome message on mount
  useEffect(() => {
    handleSendMessage('help', true); // Auto-send "help" on load
  }, []);
  
  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await listPlannerPlans('active');
      const plansList = response.data?.plans || response.plans || [];
      setPlans(plansList);
      
      // Auto-select primeiro plan se existir
      if (plansList.length > 0 && !selectedPlanId) {
        setSelectedPlanId(plansList[0].id);
      }
    } catch (error) {
      console.error('[Builder] Error fetching plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };
  
  const fetchSnapshot = async () => {
    try {
      const response = await getPlannerSnapshot(selectedPlanId);
      const snapshotData = response.data || response;
      setSnapshot(snapshotData);
      
      // Auto-select top 3 items by default
      if (snapshotData?.canvas && snapshotData.canvas.length > 0) {
        const top3 = snapshotData.canvas.slice(0, 3).map((_, idx) => idx);
        setSelectedIndices(top3);
      }
    } catch (error) {
      console.error('[Builder] Error fetching snapshot:', error);
      setSnapshot(null);
    }
  };
  
  const handleSendMessage = async (messageOverride = null) => {
    const message = messageOverride || inputMessage.trim();
    if (!message) return;

    // Mensagem do usuário
    setChatMessages(prev => [...prev, {
      message,
      isUser: true,
      timestamp: new Date().toISOString()
    }]);

    setInputMessage('');
    setLoading(true);

    try {
      const response = await sendBuilderMessage(message, {
        current_plan_id: selectedPlanId || null,
        task_ids: Array.isArray(tasks) ? tasks.map(t => t.task_id) : []
      });

      // � DEBUG CRÍTICO - Ver response exata
      console.log('[Builder DEBUG] Raw response:', response);
      console.log('[Builder DEBUG] response.data:', response?.data);
      console.log('[Builder DEBUG] response.data.data:', response?.data?.data);

      // 🔒 Blindagem total do response
      const responseData =
        response?.data?.data ||
        response?.data ||
        response ||
        {};

      console.log('[Builder DEBUG] responseData final:', responseData);

      let reply =
        responseData.reply ||
        responseData.text ||
        responseData.message ||
        '';

      console.log('[Builder DEBUG] reply extraído:', reply);

      if (!reply || reply.trim() === '') {
        reply = '⚠️ Builder não retornou resposta válida.';
      }

      console.log('[Builder DEBUG] reply final que será renderizado:', reply);

      setChatMessages(prev => [...prev, {
        isUser: false,
        response: {
          text: reply.trim(),
          actions: responseData.actions || [],
          artifacts: responseData.artifacts || []
        },
        timestamp: new Date().toISOString()
      }]);

    } catch (err) {
      console.error('[Builder Chat] Erro ao enviar mensagem:', err);

      setChatMessages(prev => [...prev, {
        isUser: false,
        response: {
          text: '❌ Erro ao comunicar com o Builder. Verifique o backend.',
          actions: [],
          artifacts: []
        },
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateTasks = async () => {
    if (!selectedPlanId) {
      alert(t.selectPlanAlert);
      return;
    }
    
    if (selectedIndices.length === 0) {
      alert(t.selectItemsAlert);
      return;
    }
    
    setLoading(true);
    try {
      const response = await generateTasks(selectedPlanId, selectedIndices);
      const responseData = response.data || response;
      
      setTasks(responseData.tasks || []);
      setActiveTab('tasks');
      
      // Adiciona mensagem no chat
      setChatMessages(prev => [...prev, {
        isUser: false,
        response: {
          text: `✅ Geradas ${responseData.total_count || 0} tarefas a partir de ${selectedIndices.length} items (custo total: $${(responseData.total_estimated_cost_usd || 0).toFixed(2)})`,
          actions: [],
          artifacts: []
        },
        timestamp: new Date().toISOString()
      }]);
      
    } catch (error) {
      console.error('[Builder] Generate tasks error:', error);
      alert(error.response?.data?.error || error.message || 'Erro ao gerar tarefas');
    } finally {
      setLoading(false);
    }
  };
  
  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      const { data } = await AZURE.put(`/governance/builder/tasks/${taskId}/status`, {
        status: newStatus
      });
      
      // Update local task
      setTasks(prev => prev.map(t => 
        t.task_id === taskId ? { ...t, status: newStatus, updated_at: data.task.updated_at } : t
      ));
      
    } catch (error) {
      console.error('Update task status error:', error);
    }
  };
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg }}>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-30 mb-6 rounded-xl">
          <div className="flex items-center gap-3 p-4">
            <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
              <h1 className="text-gray-900 text-3xl font-extrabold">
                Builder
              </h1>
              <p className="text-gray-600">{t.subtitle}</p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex border-t">
            <button
              className={`flex-1 py-3 text-sm font-semibold ${
                activeTab === 'chat' 
                  ? 'border-b-2 text-[#111111]' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={{ borderColor: activeTab === 'chat' ? COLORS.primary : 'transparent' }}
              onClick={() => setActiveTab('chat')}
            >
              💬 Chat ({chatMessages.length})
            </button>
            <button
              className={`flex-1 py-3 text-sm font-semibold ${
                activeTab === 'tasks' 
                  ? 'border-b-2 text-[#111111]' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={{ borderColor: activeTab === 'tasks' ? COLORS.primary : 'transparent' }}
              onClick={() => setActiveTab('tasks')}
            >
              {t.tabTasks(tasks.length)}
            </button>
          </div>
        </div>
        
        {/* Plan Selector */}
        <div className="mb-6">
          <PlanSelector 
            plans={plans}
            selectedPlanId={selectedPlanId}
            onSelect={setSelectedPlanId}
            onRefresh={fetchPlans}
            loading={loadingPlans}
            t={t}
          />
        </div>
        
        {/* Item Selector (show only if plan selected) */}
        {selectedPlanId && (
          <div className="mb-6">
            <ItemSelector 
              snapshot={snapshot}
              selectedIndices={selectedIndices}
              onSelectionChange={setSelectedIndices}
              loading={loading}
              t={t}
            />
          </div>
        )}
        
        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Panel (Chat or Tasks) */}
          <div className="lg:col-span-2">
            {activeTab === 'chat' && (
              <div className="bg-white rounded-xl border border-gray-200 flex flex-col h-[600px]">
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4">
                  {chatMessages.map((msg, idx) => (
                    <ChatMessage key={idx} message={msg} isUser={msg.isUser} t={t} />
                  ))}
                  {loading && (
                    <div className="flex justify-start mb-4">
                      <div className="bg-gray-100 rounded-lg px-4 py-3">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-100"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-200"></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                
                {/* Input */}
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder={t.placeholder}
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg"
                      disabled={loading}
                    />
                    <button
                      onClick={() => handleSendMessage()}
                      disabled={loading || !inputMessage.trim()}
                      className="px-6 py-2 bg-[#111111] text-white rounded-lg font-bold hover:bg-[#0b0b0c] disabled:opacity-50"
                    >
                      {t.send}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'tasks' && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>
                    {t.tasksHeader(tasks.length)}
                  </h3>
                  <button
                    onClick={handleGenerateTasks}
                    disabled={loading}
                    className="px-4 py-2 bg-[#111111] text-white rounded-lg font-bold hover:bg-[#0b0b0c] disabled:opacity-50"
                  >
                    {t.generateTasks}
                  </button>
                </div>
                
                {tasks.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-4xl mb-3">📋</div>
                    <p>{t.noTasksYet}</p>
                    <p className="text-sm">{t.noTasksHint}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tasks.map(task => (
                      <TaskCard 
                        key={task.task_id} 
                        task={task} 
                        onStatusChange={handleTaskStatusChange}
                        t={t}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Side Panel (Stats + Quick Actions) */}
          <div className="space-y-4">
            {/* Cost Tracker */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h4 className="font-bold mb-3" style={{ color: COLORS.primary }}>{t.cost}</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t.estimated}</span>
                  <span className="font-bold">
                    ${tasks.reduce((sum, tk) => sum + (tk.estimated_cost_usd || 0), 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t.actual}</span>
                  <span className="font-bold">
                    ${tasks.reduce((sum, tk) => sum + (tk.actual_cost_usd || 0), 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t.delta}</span>
                  <span className={`font-bold ${
                    tasks.reduce((sum, tk) => sum + ((tk.actual_cost_usd || 0) - (tk.estimated_cost_usd || 0)), 0) > 0
                      ? 'text-red-600' : 'text-green-600'
                  }`}>
                    ${tasks.reduce((sum, tk) => sum + ((tk.actual_cost_usd || 0) - (tk.estimated_cost_usd || 0)), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Task Stats */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h4 className="font-bold mb-3" style={{ color: COLORS.primary }}>{t.status}</h4>
              <div className="space-y-2">
                {['pending', 'in_progress', 'done', 'blocked'].map(status => {
                  const count = tasks.filter(tk => tk.status === status).length;
                  const labels = {
                    pending: t.pendingCount,
                    in_progress: t.inProgress,
                    done: t.doneCount,
                    blocked: t.blockedCount
                  };
                  return (
                    <div key={status} className="flex justify-between text-sm">
                      <span className="text-gray-600">{labels[status]}</span>
                      <span className="font-bold">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h4 className="font-bold mb-3" style={{ color: COLORS.primary }}>{t.quickActions}</h4>
              <div className="space-y-2">
                <button
                  onClick={() => handleSendMessage('criar plano')}
                  className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-left"
                >
                  {t.createPlan}
                </button>
                <button
                  onClick={() => handleSendMessage('ver snapshot')}
                  className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-left"
                >
                  {t.viewSnapshot}
                </button>
                <button
                  onClick={handleGenerateTasks}
                  disabled={loading}
                  className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-left disabled:opacity-50"
                >
                  {t.generateTasks}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernanceBuilderPage;
